// ==========================================================================
// lib/photoFilters.ts — the photo editor's looks (FF-060, 10 Oct 2026)
//   James: "make the upload photos of Izy more fun like instagram with cropping
//   and some filters".
//   Each look is a few plain numbers applied to the pixels, so the result is
//   the same on every phone (canvas `filter` isn't reliable on older iPhones).
//   All on the device: nothing is sent anywhere until the photo is saved.
// ==========================================================================

export type Look = {
  key: string; label: string;
  brightness?: number;  // -1..1
  contrast?: number;    // -1..1
  saturation?: number;  // -1..1 (-1 = black and white)
  warmth?: number;      // -1..1 (cool to warm)
  fade?: number;        // 0..1 lifts the blacks
  tint?: [number, number, number, number]; // r, g, b, strength 0..1
  sepia?: number;       // 0..1
};

export const LOOKS: Look[] = [
  { key: "none", label: "Original" },
  { key: "sunny", label: "Sunny", brightness: 0.06, contrast: 0.08, saturation: 0.12, warmth: 0.28 },
  { key: "soft", label: "Soft", brightness: 0.08, contrast: -0.12, saturation: -0.08, fade: 0.12 },
  { key: "rosy", label: "Rosy", brightness: 0.05, saturation: 0.05, warmth: 0.1, tint: [255, 170, 190, 0.1] },
  { key: "bright", label: "Bright", brightness: 0.14, contrast: 0.12, saturation: 0.18 },
  { key: "dreamy", label: "Dreamy", brightness: 0.1, contrast: -0.18, saturation: -0.15, fade: 0.18, tint: [235, 215, 255, 0.08] },
  { key: "vintage", label: "Vintage", contrast: -0.06, saturation: -0.3, warmth: 0.2, fade: 0.14, sepia: 0.25 },
  { key: "cool", label: "Cool", contrast: 0.06, saturation: 0.05, warmth: -0.25 },
  { key: "mono", label: "B&W", contrast: 0.14, saturation: -1 },
];

const clamp = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);

/** Applies a look (plus an extra brightness nudge) to the pixels in place. */
export function applyLook(img: ImageData, look: Look, extraLight = 0) {
  const d = img.data;
  const br = ((look.brightness ?? 0) + extraLight) * 255;
  const c = 1 + (look.contrast ?? 0);
  const s = 1 + (look.saturation ?? 0);
  const w = (look.warmth ?? 0) * 28;
  const fade = (look.fade ?? 0) * 255;
  const sep = look.sepia ?? 0;
  const [tr, tg, tb, ts] = look.tint ?? [0, 0, 0, 0];
  for (let i = 0; i < d.length; i += 4) {
    let r = d[i], g = d[i + 1], b = d[i + 2];
    // light and contrast around the middle grey
    r = (r - 128) * c + 128 + br; g = (g - 128) * c + 128 + br; b = (b - 128) * c + 128 + br;
    // colour: towards or away from grey
    const l = 0.299 * r + 0.587 * g + 0.114 * b;
    r = l + (r - l) * s; g = l + (g - l) * s; b = l + (b - l) * s;
    // warmth: a little more red/yellow, a little less blue
    r += w; g += w * 0.35; b -= w;
    if (sep) {
      const sr = 0.393 * r + 0.769 * g + 0.189 * b, sg = 0.349 * r + 0.686 * g + 0.168 * b, sb = 0.272 * r + 0.534 * g + 0.131 * b;
      r += (sr - r) * sep; g += (sg - g) * sep; b += (sb - b) * sep;
    }
    if (ts) { r += (tr - r) * ts; g += (tg - g) * ts; b += (tb - b) * ts; }
    if (fade) { r = fade + r * (1 - fade / 255); g = fade + g * (1 - fade / 255); b = fade + b * (1 - fade / 255); }
    d[i] = clamp(r); d[i + 1] = clamp(g); d[i + 2] = clamp(b);
  }
  return img;
}

/** Loads a picked photo into a canvas (turned upright, at most `max` px on the long side). */
export async function loadToCanvas(file: Blob, max = 2048): Promise<HTMLCanvasElement> {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions).catch(() => createImageBitmap(file));
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(bmp.width * k)); c.height = Math.max(1, Math.round(bmp.height * k));
  c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close?.();
  return c;
}

/** A quarter turn clockwise. */
export function rotate90(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = src.height; c.height = src.width;
  const x = c.getContext("2d")!;
  x.translate(c.width, 0); x.rotate(Math.PI / 2); x.drawImage(src, 0, 0);
  return c;
}

/** A copy of the canvas with a look applied. */
export function withLook(src: HTMLCanvasElement, look: Look, extraLight = 0): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = src.width; c.height = src.height;
  const x = c.getContext("2d", { willReadFrequently: true })!;
  x.drawImage(src, 0, 0);
  if (look.key === "none" && !extraLight) return c;
  const img = x.getImageData(0, 0, c.width, c.height);
  x.putImageData(applyLook(img, look, extraLight), 0, 0);
  return c;
}

/** A small copy for the filter thumbnails. */
export function thumbOf(src: HTMLCanvasElement, size = 160): HTMLCanvasElement {
  const k = size / Math.min(src.width, src.height);
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const x = c.getContext("2d")!;
  x.drawImage(src, (size - src.width * k) / 2, (size - src.height * k) / 2, src.width * k, src.height * k);
  return c;
}
